import { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Modal, Spinner } from 'react-bootstrap';
import { AuditEntry } from '@/pages/oms/orders/[id]/audit/AuditEntry';
import type { AuditHistoryEntry } from '@/infrastructure/OMSClient';
import { useOMS } from '@/apps/hooks/oms/useOMS';
import { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import type { FulfillmentOrderEvent } from '@/infrastructure/FulfillmentAdminClient';
import { getAdminApiUrl, AdminApiDomain } from '@/config/ApiRouting';
import { kstTimestamp } from '@/utils/fulfillmentTime';
import { FulfillmentV2Launch } from '@/infrastructure/FulfillmentV2Launch';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';

const AUDIT_EMPTY = 'No audit history for this order yet.';
const AUDIT_FAILED = 'Could not load the audit history. Close and try again.';
const V2_ONLY_NOTE =
  'Fulfillment 2.0 history only: audit events since July 2026. Older history is migrated after launch.';

type AuditRow =
  | { kind: 'store'; entry: AuditHistoryEntry; timestamp: number; sourceIndex: number }
  | { kind: 'customs'; event: FulfillmentOrderEvent; timestamp: number; sourceIndex: number };

const auditEntryTimestamp = (updatedTimestamp: number): number =>
  updatedTimestamp.toString().length <= 10 ? updatedTimestamp * 1000 : updatedTimestamp;

const compareAuditRows = (left: AuditRow, right: AuditRow): number =>
  right.timestamp - left.timestamp || left.sourceIndex - right.sourceIndex;

export interface AuditHistoryModalProps {
  show: boolean;
  orderId: string;
  /** True for an order the store owns; a 2.0-native order has no trail there to read [T08]. */
  inWooCommerce: boolean;
  /** Customs overrides are V2-only even while the store still owns the older trail. */
  includeCustomsHistory?: boolean;
  refreshKey?: unknown;
  onHide: () => void;
}

/** Renders one chronological history, supplementing a pre-launch store trail with V2 customs events. */
export function AuditHistoryModal({
  show,
  orderId,
  inWooCommerce,
  includeCustomsHistory = false,
  refreshKey,
  onHide,
}: AuditHistoryModalProps) {
  const oms = useOMS();
  const fulfillment = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const [entries, setEntries] = useState<AuditHistoryEntry[]>([]);
  const [events, setEvents] = useState<FulfillmentOrderEvent[]>([]);
  // The primary trail is the store's for a commerce order until Fulfillment V2 is launched, then V2's for every order.
  const [readsStore, setReadsStore] = useState(inWooCommerce);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [customsHistoryFailed, setCustomsHistoryFailed] = useState(false);

  useEffect(() => {
    if (!show) {
      return;
    }
    let ignore = false;
    setLoading(true);
    setFailed(false);
    setCustomsHistoryFailed(false);
    // The store owns a commerce order's pre-launch trail; V2 customs events supplement it when available.
    // The fulfillment event log is the whole record of an order the store never saw [T08]. Once Fulfillment V2
    // is launched V1 no longer changes, so every order reads the V2 log (SSD-7090).
    const read = (inWooCommerce ? FulfillmentV2Launch.isLaunched() : Promise.resolve(true)).then((launched) => {
      if (ignore) {
        return;
      }
      setReadsStore(!launched);
      return launched
        ? fulfillment.listOrderEvents(Number(orderId)).then((log) => {
            if (!ignore) {
              setEvents(log.events);
            }
          })
        : Promise.all([
            oms.listAuditHistory(orderId),
            includeCustomsHistory
              ? fulfillment.listOrderEvents(Number(orderId)).catch(() => {
                  if (!ignore) {
                    setCustomsHistoryFailed(true);
                  }
                  return { events: [] };
                })
              : Promise.resolve({ events: [] }),
          ]).then(([history, log]) => {
            if (!ignore) {
              setEntries(history);
              setEvents(
                log.events.filter(
                  (event) =>
                    event.operation === 'LINE_DECLARED_VALUE_SET' ||
                    event.operation === 'LINE_DECLARED_VALUE_CLEARED_BY_SPLIT',
                ),
              );
            }
          });
    });
    read
      .catch((error: unknown) => {
        if (!ignore) {
          console.error('Error fetching audit history:', error);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
    // The hook rebuilds its client every render, so depending on it here would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, orderId, inWooCommerce, includeCustomsHistory, refreshKey]);

  const auditRows: AuditRow[] = readsStore
    ? [
        ...entries.map((entry, sourceIndex) => ({
          kind: 'store' as const,
          entry,
          timestamp: auditEntryTimestamp(entry.updatedTimestamp),
          sourceIndex,
        })),
        ...events.map((event, sourceIndex) => ({
          kind: 'customs' as const,
          event,
          timestamp: new Date(event.occurredAt).getTime(),
          sourceIndex: entries.length + sourceIndex,
        })),
      ].sort(compareAuditRows)
    : events.map((event, sourceIndex) => ({
        kind: 'customs',
        event,
        timestamp: new Date(event.occurredAt).getTime(),
        sourceIndex,
      }));

  return (
    <Modal {...ULTIMATE_MODAL} show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          Audit history · <span className="font-monospace">#{orderId}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" role="status" aria-label="Loading audit history" />
          </div>
        )}
        {inWooCommerce && !readsStore && <div className="text-muted small mb-2">{V2_ONLY_NOTE}</div>}
        {customsHistoryFailed && !loading && !failed && (
          <Alert variant="warning" className="small">
            Customs audit history could not be loaded. The store history is shown below. Close and retry to see customs
            changes.
          </Alert>
        )}
        {failed && !loading && (
          <Alert variant="danger" className="mb-0">
            {AUDIT_FAILED}
          </Alert>
        )}
        {!loading && !failed && auditRows.length === 0 && <div className="text-muted">{AUDIT_EMPTY}</div>}
        {!loading &&
          !failed &&
          auditRows.map((row) =>
            row.kind === 'store' ? (
              <AuditEntry key={`${row.entry.updatedTimestamp}-${row.sourceIndex}`} entry={row.entry} kst />
            ) : (
              <Card className="mb-3" key={row.event.id}>
                <Card.Body className="py-2">
                  <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2">
                    <span className="font-monospace fw-semibold">{row.event.operation ?? '—'}</span>
                    <span className="text-muted small">
                      {row.event.operator ?? '—'} · {row.event.source ?? '—'} · {kstTimestamp(row.event.occurredAt)}
                    </span>
                  </div>
                  {row.event.payload && Object.keys(row.event.payload).length > 0 && (
                    <pre className="small mb-0 mt-1">{JSON.stringify(row.event.payload, null, 2)}</pre>
                  )}
                </Card.Body>
              </Card>
            ),
          )}
      </Modal.Body>
    </Modal>
  );
}
